import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { getStatusesStringList } from '../../helpers/common.helpers';
import { countValidUntil } from '../../helpers/validDate.helper';
import { BaseIcon, BaseSwitch } from '..';

import AppConfig from '../../providers/AppConfig';
import { STATUS } from '../../constants/status.constant';
import { FORM_VARIANT } from '../../constants/form.constant';
import { formatDate } from '../../helpers/formatDate.helper';

import './BaseStatus.scss';

export const BaseStatus = (props) => {
  const isAdmin = useSelector((state) => state.user.details.is_admin);
  const hasGroup = useSelector((state) => state.user.details.group);
  const statusTypes = useSelector((state) => state.resources.status);

  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const {
    formVariant,
    status,
    className,
    case_id,
    last_edited,
    jira_link,
    submitted_date,
    validation_date,
    showDetails,
    isAdvisor,
    isPublishedOnRegister,
    handleSwitchChange = () => {},
    creator,
    amendment,
    request_submission_date,
    request_approval_date,
    last_amendment_submission_date,
    last_amendment_approval_date,
    valid_until_date,
  } = props;

  const statuses = useMemo(() => getStatusesStringList(statusTypes.data), [statusTypes]);
  const showJiraLink = isAdmin && jira_link && jira_link.url;
  const { MAIN } = FORM_VARIANT;
  const isMainForm = formVariant === MAIN;
  const { dayMonthYearHourFormat, dayMonthHourFormat } = AppConfig;

  const caseIdBlock = () => (
    <div className="base-status__info">
      <p className="base-status__info-label">{isAdvisor ? t('registrationId') : common('caseId')}</p>
      <p className="base-status__info-value" data-testid="base-status-case-id">
        {case_id}
      </p>
    </div>
  );

  const draftStatusBlock = () => (
    <div className="base-status__info">
      <p className="base-status__info-label">{common('request.details.lastSaved')}:</p>
      <p className="base-status__info-value">{formatDate(last_edited, dayMonthYearHourFormat)}</p>
    </div>
  );

  const jiraBlock = () => (
    <div className="base-status__info">
      <p className="base-status__info-label">{common('request.details.jiraTicket')}:</p>
      <a href={jira_link.url} target="_blank" rel="noopener noreferrer" className="link--underlined">
        <p className="base-status__info-value">{jira_link.name}</p>
      </a>
    </div>
  );

  const creatorAndGroupBlock = () => (
    <div className="base-status__info" data-testid="summary-creator">
      <p className="base-status__info-label">{t('request.details.owner')}:</p>
      <p className="base-status__info-value">{creator.full_name}</p>
    </div>
  );

  const publicRegisterBlock = () => (
    <div className="base-status__switch">
      <p className="base-status__info-label">
        {t('publicRegister')}
        {isMainForm ? ':' : null}
      </p>
      {isAdmin ? (
        <BaseSwitch value={isPublishedOnRegister} onChange={handleSwitchChange} />
      ) : (
        <BaseIcon large={!isMainForm} icon={isPublishedOnRegister ? 'yes' : 'no'} />
      )}
    </div>
  );

  const clientAdvisorBlock = () => (
    <div className="base-status__date">
      {baseStatusBlock(common('requestSubmissionDate'), formatDate(request_submission_date, dayMonthHourFormat))}
      {baseStatusBlock(common('requestApprovalDate'), formatDate(request_approval_date, dayMonthHourFormat))}
    </div>
  );

  const requestAmendmentBlock = () => (
    <div className="base-status__date">
      {baseStatusBlock(
        common('lastAmendmentSubmissionDate'),
        formatDate(last_amendment_submission_date, dayMonthHourFormat)
      )}
      {baseStatusBlock(
        common('lastAmendmentApprovalDate'),
        formatDate(last_amendment_approval_date, dayMonthHourFormat)
      )}
      {baseStatusBlock(common('amendmentStatus'), baseLabelBlock(amendment.status))}
    </div>
  );

  const validUntilDate = () => (
    <div className="base-status__date">
      {baseStatusBlock(common('validUntilDate'), countValidUntil(valid_until_date))}
    </div>
  );

  const baseStatusBlock = (label, [formattedDate, daysUntil]) => (
    <div className="base-status__info">
      <p className="base-status__info-label">{label}</p>
      <p className="base-status__info-value">
        {formattedDate} {common('daysLeft', { days: daysUntil })}
      </p>
    </div>
  );

  const baseLabelBlock = (status, className = '') => (
    <span
      data-testid={props['data-testid']}
      className={classnames('base-status', `base-status--${statuses[status]}`, className)}
    >
      {common(`status.${statuses[status]}`)}
    </span>
  );

  const prospectusStatusBar = showDetails && (
    <>
      {case_id && caseIdBlock()}
      {showJiraLink && jiraBlock()}
      {status === STATUS.DRAFT && draftStatusBlock()}
      {creator && hasGroup && creatorAndGroupBlock()}
      {submitted_date &&
        baseStatusBlock(common('request.details.submittedDate'), formatDate(submitted_date, dayMonthHourFormat))}
      {AppConfig.optionalDateStatuses.includes(status) &&
        baseStatusBlock(common(`request.details.${statuses[status]}`), formatDate(validation_date, dayMonthHourFormat))}
    </>
  );

  const advisorStatusBar = showDetails && (
    <>
      {isAdvisor && status === STATUS.APPROVED && publicRegisterBlock()}
      {case_id && caseIdBlock()}
      {!isMainForm || status === STATUS.DRAFT ? draftStatusBlock() : null}
      {!isMainForm && validUntilDate()}
      {isMainForm && clientAdvisorBlock()}
      {isMainForm && amendment && requestAmendmentBlock()}
    </>
  );

  return (
    <div
      className={classnames('base-status__wrapper', {
        [`base-status__wrapper--${formVariant}`]: formVariant,
      })}
    >
      {baseLabelBlock(status, className)}
      {isAdvisor ? advisorStatusBar : prospectusStatusBar}
    </div>
  );
};

export default BaseStatus;
